<script lang="ts">
	import { ControlsContainer, DeviceButton, DeviceButtonV2 } from '@Shelly/shelly-design-system';
	import { postReq } from 'src/services/http';
	import { scenes } from 'src/stores/lists/scenes';
	import { user_settings } from 'src/stores/account/user';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { format } from 'svelte-i18n';
	import DuplicateSceneWarningModal from 'src/components/Modals/DuplicateSceneWarningModal.svelte';
	import { useDuplicateSceneDetection } from 'src/helpers/useDuplicateSceneDetection';
	import { ScriptScenesService } from '@Shelly/scenes-script';

	export let item;
	export let variant: 'listMenuCard' | 'drawer' | 'widgetCard' | 'dashControlWidget';

	let id = item.id;

	$: scene = $scenes[id] || {};

	$: enable_scene_btn = scene._enabled || false;

	let loading = false;

	const hasEnableRights = !$user_settings.sub || item._meta?.rights?.enable !== 0;

	// Use the composable hook for duplicate detection
	const { showWarningModal, duplicateInfo, checkDuplicateAndWarn, confirmAction, cancelAction } =
		useDuplicateSceneDetection();

	const enableDisableScene = async (data) => {
		try {
			return await postReq('/scene/enable', data);
		} catch (error) {
			console.error('There is erron on enable/disable scene id ', data.id, error);
			return { isok: false };
		}
	};

	const toggleScriptSceneStatus = async () => {
		const data = {
			enable: !scene._enabled,
		};
		try {
			return await ScriptScenesService.toggleSceneStatuses(scene.infinno.sceneId, data);
		} catch (error) {
			console.error(
				'There is erron on enable/disable script scene id ',
				scene.infinno.sceneId,
				error,
			);
			return { isok: false };
		}
	};

	const enable_disable_scene = async () => {
		try {
			loading = true;
			let res: any = await enableDisableScene({
				id: scene._id,
				enabled: !enable_scene_btn,
			});
			if (scene && scene.infinno) {
				await toggleScriptSceneStatus();
			}

			if (res.data.scene_script !== undefined && res.data.scene_script.id !== undefined) {
				scenes.editScene(res.data.scene_script);
			}
			loading = false;
		} catch (error) {
			// toast.open('danger', 'Something went wrong!');
			loading = false;
		}
	};

	const handleSceneActivation = async (e) => {
		e.stopPropagation();

		// Check if trying to enable a duplicate scene
		if (!enable_scene_btn) {
			// Only check for duplicates when enabling (not when disabling)
			checkDuplicateAndWarn(id, $scenes, enable_disable_scene, 'enable');
		} else {
			// Disabling doesn't need duplicate check
			await enable_disable_scene();
		}
	};

	const confirmActivateDuplicate = () => {
		confirmAction();
	};

	const cancelActivateDuplicate = () => {
		cancelAction();
	};

	const a11yProps = {
		indicatorStatusText: enable_scene_btn
			? $format('enable_scene', { default: 'Scene is enabled' })
			: $format('disable_scene', { default: 'Scene is enabled' }),
	};
</script>

{#if variant === 'listMenuCard' || variant === 'widgetCard'}
	{#if $showNewLayout}
		<DeviceButtonV2
			id="devicebutton-kq71u0u"
			aria-label={enable_scene_btn
				? $format('disable_scene', { default: 'Disable scene' })
				: $format('enable_scene', { default: 'Enable scene' })}
			icon="fas fa-power-off"
			variant="primary"
			isActive={enable_scene_btn}
			disabled={loading || !hasEnableRights}
			on:click={handleSceneActivation}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-kq71u0u"
			aria-label={enable_scene_btn
				? $format('disable_scene', { default: 'Disable scene' })
				: $format('enable_scene', { default: 'Enable scene' })}
			icon="fas fa-power-off"
			variant="primary"
			isActive={enable_scene_btn}
			disabled={loading || !hasEnableRights}
			on:click={handleSceneActivation}
		/>
	{/if}
{:else if variant === 'drawer'}
	{#if $showNewLayout}
		<ControlsContainer class="mt-s" color="color-0">
			<DeviceButtonV2
				id="devicebutton-xon5pgz"
				aria-label={enable_scene_btn
					? $format('disable_scene', { default: 'Disable scene' })
					: $format('enable_scene', { default: 'Enable scene' })}
				class="justify-content-center"
				isWide
				size="l"
				slot="first"
				indicator={enable_scene_btn ? 'on' : 'off'}
				{a11yProps}
				disabled={loading || !hasEnableRights}
				icon="fa fa-power-off"
				variant="primary"
				isActive={enable_scene_btn}
				on:click={handleSceneActivation}
			/>
		</ControlsContainer>
	{:else}
		<ControlsContainer class="mt-s">
			<DeviceButton
				id="devicebutton-xon5pgz"
				aria-label={enable_scene_btn
					? $format('disable_scene', { default: 'Disable scene' })
					: $format('enable_scene', { default: 'Enable scene' })}
				class="justify-content-center"
				isWide
				size="l"
				slot="first"
				indicator={enable_scene_btn ? 'on' : 'off'}
				{a11yProps}
				disabled={loading || !hasEnableRights}
				icon="fa fa-power-off"
				variant="primary"
				isActive={enable_scene_btn}
				on:click={handleSceneActivation}
			/>
		</ControlsContainer>
	{/if}
{:else if variant === 'dashControlWidget'}
	<DeviceButtonV2
		id="devicebutton-kq71u0u"
		size="s"
		aria-label={enable_scene_btn
			? $format('disable_scene', { default: 'Disable scene' })
			: $format('enable_scene', { default: 'Enable scene' })}
		icon="fas fa-power-off"
		variant="primary"
		isActive={enable_scene_btn}
		disabled={loading || !hasEnableRights}
		on:click={handleSceneActivation}
	/>
{/if}

<!-- Duplicate Scene Warning Modal -->
<DuplicateSceneWarningModal
	showModal={$showWarningModal}
	actionType="enable"
	originalSceneName={$duplicateInfo?.originalName || ''}
	onConfirm={confirmActivateDuplicate}
	onCancel={cancelActivateDuplicate}
/>
